/**
 * ButtonBar.tsx - Draggable Button Bar Component
 * 
 * Universal button bar for OpCentral apps
 * Replaces Dashboard TabBar with standardized, customizable component
 * 
 * Features:
 * - Drag and drop reordering (via Draggr)
 * - Custom button order persistence (via Cachr)
 * - Status indicators
 * - Action buttons
 * - Skinnr/Stylr theming
 * - Scriptr customization
 * - Actr logging
 * - Backtrackr undo/redo
 * 
 * ARCHITECTURE:
 * - Uses @opcentral/draggr for drag and drop
 * - Uses @opcentral/cachr for state persistence
 * - Uses @opcentral/actr for action logging
 * - Uses @opcentral/backtrackr for undo/redo
 */

import React, { useState, useEffect, useCallback } from 'react';
import { DraggableButton } from './DraggableButton';
import { useCachr } from '@opcentral/cachr';
import { useBacktrackr } from '@opcentral/backtrackr';
import type { ButtonBarProps, ButtonConfig, ButtonBarState } from './types';
import './ButtonBar.css';

export const ButtonBar: React.FC<ButtonBarProps> = ({
  appName,
  buttons,
  activeButton,
  draggable = true,
  showActions = true,
  defaultActions = ['duplicate', 'edit', 'external'],
  renderButton,
  onButtonClick,
  onButtonReorder,
  onButtonDuplicate,
  onButtonEdit,
  onButtonRemove,
  onButtonExternal,
  theme,
  variant = 'default',
  className = '',
  persistOrder = true,
  persistCustomLabels = true,
}) => {
  const { get, set } = useCachr();
  const backtrackr = useBacktrackr() as any;
  
  const [state, setState] = useState<ButtonBarState>({
    order: buttons.map(b => b.id),
    customLabels: {},
    hiddenButtons: [],
    customData: {},
    lastUpdated: Date.now(),
  });

  // Load persisted state from Cachr
  useEffect(() => {
    if (persistOrder || persistCustomLabels) {
      try {
        const saved = get(`buttonbar_${appName}`);
        if (saved) {
          setState(prev => ({
            ...prev,
            ...(persistOrder && saved.order ? { order: saved.order } : {}),
            ...(persistCustomLabels && saved.customLabels ? { customLabels: saved.customLabels } : {}),
          }));
          
          // Log state load
          actr?.execute('buttonbar:state-loaded', {
            appName,
            hasOrder: !!saved.order,
            hasLabels: !!saved.customLabels
          });
        }
      } catch (error) {
        console.error('Error loading ButtonBar state:', error);
        actr?.execute('buttonbar:state-load-error', {
          appName,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  }, [appName, persistOrder, persistCustomLabels, get, actr]);

  // Save state to Cachr
  const saveState = useCallback((newState: Partial<ButtonBarState>) => {
    const previousState = state;
    const updated = { ...state, ...newState, lastUpdated: Date.now() };
    setState(updated);

    if (persistOrder || persistCustomLabels) {
      try {
        // Save to Cachr
        set(`buttonbar_${appName}`, updated);
        
        // Log state save
        actr?.execute('buttonbar:state-saved', {
          appName,
          changes: Object.keys(newState)
        });
        
        // Record for undo/redo
        if (backtrackr) {
          backtrackr.recordChange({
            type: 'buttonbar:state-update',
            description: `Update button bar state: ${Object.keys(newState).join(', ')}`,
            context: null,
            before: previousState,
            after: updated,
            metadata: { appName }
          });
        }
      } catch (error) {
        console.error('Error saving ButtonBar state:', error);
        actr?.execute('buttonbar:state-save-error', {
          appName,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  }, [appName, state, persistOrder, persistCustomLabels, set, actr, backtrackr]);

  // Get sorted buttons
  const getSortedButtons = useCallback(() => {
    if (!persistOrder || state.order.length === 0) {
      return buttons.filter(b => !b.hidden && !state.hiddenButtons.includes(b.id));
    }

    const ordered: ButtonConfig[] = [];
    const unordered = [...buttons];

    state.order.forEach(id => {
      const index = unordered.findIndex(b => b.id === id);
      if (index !== -1) {
        const button = unordered.splice(index, 1)[0];
        if (!button.hidden && !state.hiddenButtons.includes(button.id)) {
          ordered.push(button);
        }
      }
    });

    // Add any new buttons not in order
    unordered.forEach(button => {
      if (!button.hidden && !state.hiddenButtons.includes(button.id)) {
        ordered.push(button);
      }
    });

    return ordered;
  }, [buttons, state.order, state.hiddenButtons, persistOrder]);

  // Handle button click
  const handleButtonClick = (button: ButtonConfig) => {
    // Log action
    actr?.execute('buttonbar:button-click', {
      appName,
      buttonId: button.id,
      buttonLabel: button.label
    });
    
    if (button.onClick) {
      button.onClick();
    }
    if (onButtonClick) {
      onButtonClick(button);
    }
  };

  // Handle reorder
  const handleReorder = (fromIndex: number, toIndex: number) => {
    const sortedButtons = getSortedButtons();
    const newOrder = sortedButtons.map(b => b.id);
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);

    // Log action
    actr?.execute('buttonbar:reorder', {
      appName,
      from: fromIndex,
      to: toIndex,
      buttonId: removed
    });

    saveState({ order: newOrder });

    if (onButtonReorder) {
      onButtonReorder(fromIndex, toIndex);
    }
  };

  // Handle duplicate
  const handleDuplicate = (buttonId: string) => {
    actr?.execute('buttonbar:duplicate', {
      appName,
      buttonId
    });
    
    if (onButtonDuplicate) {
      onButtonDuplicate(buttonId);
    }
  };

  // Handle edit
  const handleEdit = (buttonId: string) => {
    actr?.execute('buttonbar:edit', {
      appName,
      buttonId
    });
    
    if (onButtonEdit) {
      onButtonEdit(buttonId);
    }
  };

  // Handle remove
  const handleRemove = (buttonId: string) => {
    const newHidden = [...state.hiddenButtons, buttonId];
    
    actr?.execute('buttonbar:remove', {
      appName,
      buttonId
    });
    
    saveState({ hiddenButtons: newHidden });

    if (onButtonRemove) {
      onButtonRemove(buttonId);
    }
  };

  // Handle external
  const handleExternal = (button: ButtonConfig) => {
    actr?.execute('buttonbar:open-external', {
      appName,
      buttonId: button.id,
      url: button.href
    });
    
    if (button.href) {
      window.open(button.href, button.target || '_blank');
    }
    if (onButtonExternal) {
      onButtonExternal(button);
    }
  };

  const sortedButtons = getSortedButtons();
  const themeClass = theme ? `theme-${theme}` : '';
  const variantClass = `variant-${variant}`;

  return (
    <div className={`button-bar-container ${themeClass} ${className}`}>
      <div className={`button-bar ${variantClass}`}>
        {sortedButtons.map((button, index) => {
          if (renderButton) {
            return (
              <React.Fragment key={button.id}>
                {renderButton(button, index)}
              </React.Fragment>
            );
          }

          return (
            <DraggableButton
              key={button.id}
              button={button}
              index={index}
              isActive={activeButton === button.id}
              customLabel={state.customLabels[button.id]}
              showActions={showActions && draggable}
              defaultActions={defaultActions}
              onClick={handleButtonClick}
              onReorder={draggable ? handleReorder : () => {}}
              onDuplicate={onButtonDuplicate ? handleDuplicate : undefined}
              onEdit={onButtonEdit ? handleEdit : undefined}
              onRemove={onButtonRemove ? handleRemove : undefined}
              onExternal={onButtonExternal || button.href ? handleExternal : undefined}
              variant={variant}
            />
          );
        })}
      </div>
    </div>
  );
};
